import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { getIn } from 'final-form';
import isEqual from 'lodash/isEqual';
import { Listing } from '../../../../../../../models/Listing';
import { GenerateHtmlFunction, ListingPreviewElementsContent } from '../utils';
import { Field, useField, useForm } from 'react-final-form';
import { DropzoneFile } from 'components/Dropzone/utils';
import { Button, ImageSlider } from 'ncoded-component-library';
import ResolveImage from 'components/ResolveImage';
import { getImgUrl, RESIZED_WIDTH } from 'components/ResolveImage/utils';
import {
  convertMoneyToNumber,
  getHasElementsInArray,
  getIsArrayEmpty,
  isEnterPressed,
  isEscapePressed,
  isSingleElementArray,
  isTabPressed,
  mapValueToMoneyFormat,
} from 'utils';
import { SMOOTH_SCROLL_CONFIGURATION } from 'data/global';
import {
  getFilledListItems,
  PublicListingRequirementKey,
} from 'router/subrouters/ListingCreationWizard/data/publicListingRequirements';
import PlusIcon from 'icons/Plus.icon';
import TextareaArrayField from 'components/TextareaArrayField';
import ListingSlugField, {
  SLUG_FIELD_NAME,
} from 'router/subrouters/ListingCreationWizard/components/ListingSlugField/ListingSlugField.component';
import HighlightsSection from 'router/subrouters/Dash/subrouters/Business/pages/Business/components/HighlightsSection';
import { AdvertisingBusinessCardProps } from 'components/AdvertisingBusinessCard/AdvertisingBusinessCard.component';
import PhoneIcon from 'icons/Phone.icon';
import LinkIcon from 'icons/Link.icon';
import { formatPhoneNumber } from 'phoneNumberUtils';
import PreviewPatchListingContext from 'router/subrouters/ListingCreationWizard/providers/PreviewPatchListing/PreviewPatchListing.context';
import PreviewMissingFieldsContext from '../../providers/PreviewMissingFields/PreviewMissingFields.context';

import './DetailPageField.styles.scss';

/**
 * - `exit` saves when the field leaves edit state (click away, Enter, unmount).
 * - `debounced` additionally saves one second after typing stops.
 * - `none` (default) leaves the field unmanaged, for call sites outside the LCW.
 */
export type DetailPageFieldSaveOn = 'exit' | 'debounced' | 'none';

/** Marks the wrapper so Enter can find the next field in DOM order. */
const FIELD_MARKER_ATTRIBUTE = 'data-preview-field';

/** Addresses one wrapper, so "Show missing fields" can find it by name. */
const FIELD_NAME_ATTRIBUTE = 'data-preview-field-name';

const TEXT_CONTROL_SELECTOR = [
  'input:not([type="hidden"]):not([type="file"]):not([hidden])',
  'textarea',
].join(', ');

const FOCUSABLE_CONTROL_SELECTOR = [
  TEXT_CONTROL_SELECTOR,
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const isSaveOnNone = (saveOn: DetailPageFieldSaveOn) => saveOn === 'none';
const isSaveOnDebounced = (saveOn: DetailPageFieldSaveOn) =>
  saveOn === 'debounced';

type DetailPageFieldProps = {
  className?: string;
  name: keyof Listing | keyof AdvertisingBusinessCardProps;
  field?: any;
  previewType?: keyof ListingPreviewElementsContent;
  icon?: React.FC;
  placeholder?: string;
  label?: string;
  allowEnter?: boolean;
  saveOn?: DetailPageFieldSaveOn;
  /**
   * Keeps the field closed when tabbed onto while it already holds a value, so
   * Tab passes over it. For selects, opening would spring a dropdown and a
   * search input into the tab order. Enter still opens it for editing.
   */
  keepClosedWhenFilled?: boolean;
  /**
   * `images` only. Marks the stock Resolve image as a placeholder and offers an
   * add control on hover, until the listing has an image of its own.
   *
   * `hideFallbackImage` drops the stock image and shows an empty slot with a
   * always-visible add control instead - for the company logo, where the
   * Resolve drawing would read as the firm's own.
   */
  imagePlaceholder?: {
    label?: string;
    addLabel: string;
    hideFallbackImage?: boolean;
  };
  renderEditor?: () => React.ReactNode;
  renderPreview?: () => React.ReactNode;
  /**
   * The requirements this field answers, from the shared rule set. Leaving the
   * field having satisfied none of them marks them - the "opened it and left
   * it" trigger - and a marked requirement paints the red border.
   *
   * Usually one, but the financial block covers price guide, revenue and EBITDA
   * behind a single editor.
   */
  requirementKeys?: PublicListingRequirementKey[];
  /**
   * Keeps the mark off this wrapper. The two-of-three list sections take no
   * border at all, and the financial block marks its individual tiles instead.
   */
  hideMissingBorder?: boolean;
};

const FIELD_PREVIEW_ELEMENT: GenerateHtmlFunction<ListingPreviewElementsContent> =
  {
    h1: (val: string) => <h1>{val}</h1>,
    images: (images: DropzoneFile[], config: any) => {
      const { imagePlaceholder } = config || {};

      // DropzoneField collapses a single file to a scalar, so normalise first.
      const files = Array.isArray(images) ? images : images ? [images] : [];
      const hasOwnImages = getHasElementsInArray(files);

      const showEmptyState = !hasOwnImages && !!imagePlaceholder;
      // The company logo has no stock image to fall back on - showing the
      // Resolve drawing there would read as the firm's own logo.
      const isEmptySlot = showEmptyState && imagePlaceholder.hideFallbackImage;

      return (
        <div
          className={classNames('res-ldp-image-container', {
            'is-empty-slot': isEmptySlot,
          })}
        >
          {!isEmptySlot &&
            (isSingleElementArray(files) || !hasOwnImages ? (
              <ResolveImage
                className="listing-card-image"
                src={getImgUrl(files[0]?.url, RESIZED_WIDTH[480])}
              />
            ) : (
              <ImageSlider
                slides={files.map(({ url }) => ({
                  imageUrl: getImgUrl(url, RESIZED_WIDTH[480]),
                }))}
                showDots={hasOwnImages}
                className="listing-card-resolve__image-slider-container__slider"
              />
            ))}

          {showEmptyState && imagePlaceholder.label && (
            <span className="res-ldp-image-container__placeholder-label">
              {imagePlaceholder.label}
            </span>
          )}

          {showEmptyState && (
            <span
              className={classNames('res-ldp-image-container__add', {
                persistent: isEmptySlot,
              })}
            >
              <PlusIcon />
              {imagePlaceholder.addLabel}
            </span>
          )}
        </div>
      );
    },
    link: (link: string) => <p>{link}</p>,
    phoneNumber: (phoneNumber: string) => (
      <p className="phone-num-preview">
        <PhoneIcon />
        {formatPhoneNumber(phoneNumber)}
      </p>
    ),
    span: (span: string) => {
      const numberValue = isNaN(+span) ? '0' : span;
      return (
        <span>{mapValueToMoneyFormat(convertMoneyToNumber(numberValue))}</span>
      );
    },
    p: (p: string) => <p>{p}</p>,
    slug: (p: string) => <p>{p}</p>,
    highlightlsSection: (listArray: string[], config: any) => {
      const { placeholder } = config || {};

      /**
       * The form seeds each section with blank rows, so the raw array is never
       * empty - filtering first is what tells an untouched section from an
       * entered one. An empty section shows a single instruction row rather
       * than a set of empty bullets.
       */
      const items = getFilledListItems(listArray);

      return (
        <HighlightsSection
          listArray={
            getHasElementsInArray(items) ? items : [placeholder].filter(Boolean)
          }
          isPlaceholder={getIsArrayEmpty(items)}
          {...config}
        />
      );
    },
    advertCta: (ctaText: string) => (
      <Button onClick={() => {}}>
        <LinkIcon />
        {ctaText}
      </Button>
    ),
  };

const DetailPageField: React.FC<DetailPageFieldProps> = (props) => {
  const {
    className,
    previewType,
    name,
    field,
    icon,
    placeholder,
    label,
    imagePlaceholder,
    saveOn = 'none',
    keepClosedWhenFilled = false,
    allowEnter = false,
    requirementKeys,
    hideMissingBorder = false,
    renderEditor,
    renderPreview,
  } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [transitionClass, setTransitionClass] = useState('appear');

  const isFirst = useRef(true);
  const wasEditing = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const isShiftTabbingRef = useRef(false);
  /** Where to put focus once the editor has actually mounted. */
  const pendingFocusRef = useRef<'first' | 'last' | null>(null);
  /** A named control to land on instead, once the editor has mounted. */
  const pendingFocusFieldRef = useRef<string | null>(null);
  /** Form values as the field was opened, so Escape has something to put back. */
  const editStartValuesRef = useRef<Record<string, any>>(null);
  /** Escape leaves nothing outstanding: a cancelled edit earns no mark. */
  const skipMarkRef = useRef(false);

  const isSlugPreview = SLUG_FIELD_NAME === name;
  const isImages = previewType === 'images';

  // Shift+Tab should land on the last control of a block, not the first. The
  // Tab keydown fires on the element being left, so it has to be watched here.
  useEffect(() => {
    const onDocumentKeyDown = (ev: KeyboardEvent) => {
      if (isTabPressed(ev)) isShiftTabbingRef.current = ev.shiftKey;
    };

    document.addEventListener('keydown', onDocumentKeyDown, true);
    return () =>
      document.removeEventListener('keydown', onDocumentKeyDown, true);
  }, []);

  const {
    input: { value },
  } = useField(name);

  const form = useForm();

  const { saveFields, flushNow } = useContext(PreviewPatchListingContext);

  const { focusRequest, consumeFocusRequest, isMarked, markIfMissing } =
    useContext(PreviewMissingFieldsContext);

  const isMissing = useMemo(
    () =>
      !hideMissingBorder && !!requirementKeys?.some((key) => isMarked?.(key)),
    [hideMissingBorder, isMarked, requirementKeys],
  );

  /** Seeded with the mount value so opening and closing without a change is a no-op. */
  const lastQueuedRef = useRef(value);

  /**
   * Whether the preview fell back to the placeholder, so it can be greyed.
   * Mirrors what RenderPreview resolves - images and a custom preview carry
   * their own empty state, so neither is greyed here.
   */
  const isEmpty = useMemo(() => {
    if (isImages || renderPreview) return false;

    if (label) return false;

    if (Array.isArray(value)) return getIsArrayEmpty(getFilledListItems(value));

    return !`${value ?? ''}`.trim();
  }, [isImages, label, renderPreview, value]);

  const classes = classNames(
    'detail-page-field',
    previewType,
    {
      isEditing,
      'slug-preview': isSlugPreview,
      'is-empty': isEmpty,
      'is-missing': isMissing,
    },
    className,
  );

  const contentClasses = classNames(
    'detail-page-field__content',
    { isEditing },
    transitionClass,
  );

  const RenderPreview = useMemo(() => {
    if (renderPreview) return renderPreview();

    const preview = label || value || placeholder;

    return FIELD_PREVIEW_ELEMENT[previewType](preview, {
      icon,
      placeholder,
      titleLabel: name,
      imagePlaceholder,
    });
  }, [
    icon,
    imagePlaceholder,
    label,
    name,
    placeholder,
    previewType,
    renderPreview,
    value,
  ]);

  const CorrectField = useMemo(
    () =>
      isSlugPreview
        ? ListingSlugField
        : previewType === 'highlightlsSection'
        ? TextareaArrayField
        : Field,
    [isSlugPreview, previewType],
  );

  const getChild = useCallback(
    () =>
      isEditing ? (
        renderEditor ? (
          renderEditor()
        ) : (
          <CorrectField name={name} {...field} />
        )
      ) : (
        RenderPreview
      ),

    [CorrectField, RenderPreview, field, isEditing, name, renderEditor],
  );

  const [renderedChild, setRenderedChild] = useState(getChild());

  const onTransitionsEnd = useCallback(() => {
    setRenderedChild(getChild());
    setTransitionClass('appear');
  }, [getChild]);

  const isSliderButtonClicked = useCallback((e) => {
    const target = e.target as Element | null;

    if (!target) return false;

    const classList = target.classList;

    return (
      target.closest('.ncoded-image-slider__arrow') ||
      classList.contains('ncoded-image-slider__arrow') ||
      target.closest('.ncoded-image-slider__dot-container__dot') ||
      classList.contains('ncoded-image-slider__dot-container__dot')
    );
  }, []);

  /** Tab order follows the DOM, which already runs left column then right. */
  const focusSiblingField = useCallback((direction: 1 | -1) => {
    const fields = Array.from(
      document.querySelectorAll<HTMLElement>(`[${FIELD_MARKER_ATTRIBUTE}]`),
    );
    const index = fields.indexOf(rootRef.current);

    fields[index + direction]?.focus();
  }, []);

  const openFilePicker = useCallback(() => {
    rootRef.current
      ?.querySelector<HTMLInputElement>('input[type="file"]')
      ?.click();
  }, []);

  /**
   * Steps to the next typed control within this field, for the editors that
   * open as a block of several - Highlight 1 to Highlight 2, sale price to
   * revenue. Says whether it moved, so Enter falls through to closing the field
   * at the end of a block.
   *
   * Deliberately text controls only. A radio-slider option is a focusable label
   * that takes Enter to select itself, so stepping on from one would land the
   * next press on the price tab beside it; those keep closing the field as
   * before. The add and remove buttons are plain `button`s, which neither
   * selector matches.
   */
  const focusNextInnerControl = useCallback(() => {
    const controls = Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>(TEXT_CONTROL_SELECTOR) ||
        [],
    );

    const index = controls.indexOf(document.activeElement as HTMLElement);

    // -1 means focus sits on the wrapper or on a control of another kind, which
    // would otherwise read as "step to the first one" and swallow the Enter.
    if (index === -1) return false;

    const next = controls[index + 1];

    if (!next) return false;

    next.focus();
    return true;
  }, []);

  /**
   * Puts back the value the field was opened with and leaves, taking focus off
   * the page rather than reopening the field or springing the next one.
   *
   * The value is restored before edit state drops, so the exit effect's own
   * save does the right thing without a flag: an `exit` field has queued
   * nothing and dedupes to a no-op, while a `debounced` one has already patched
   * what was typed and so re-sends the original.
   */
  const cancelEdit = useCallback(() => {
    const values = editStartValuesRef.current;

    /**
     * Every control the editor put on the page, so a block of several is put
     * back whole rather than only the key the wrapper is named after - the
     * financial panel edits revenue and EBITDA under `askingPriceSelect`, and a
     * list section edits `highlights[0..n]` under `highlights`. Read while the
     * editor is still mounted. A radio slider names all of its inputs alike,
     * hence the dedupe.
     */
    const names = Array.from(
      rootRef.current?.querySelectorAll('[name]') || [],
    ).map((control) => control.getAttribute('name'));

    form.batch(() =>
      Array.from(new Set([name as string, ...names])).forEach((fieldName) =>
        form.change(fieldName, getIn(values, fieldName)),
      ),
    );

    skipMarkRef.current = true;
    setIsEditing(false);
    (document.activeElement as HTMLElement)?.blur();
  }, [form, name]);

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLDivElement>) => {
      if (isEscapePressed(ev)) {
        // Images upload as they are dropped, so there is no local edit to undo
        // and putting the old gallery back would only lose an uploaded file.
        if (!isEditing || isImages) return;

        /**
         * A select's options render in a portal: the first press closes the
         * dropdown, the second cancels the field. Matched on the open modifier
         * rather than the container, which every Dropdown keeps in the document
         * and merely hides - testing the container alone swallows every Escape
         * on the page.
         */
        if (document.querySelector('.dropdown__content--open')) return;

        ev.preventDefault();
        cancelEdit();
        return;
      }

      if (!isEnterPressed(ev)) return;

      // The dropzone is already mounted by the time focus opened the field, so
      // this stays inside the user gesture the file dialog needs.
      if (isImages) {
        // The dropzone's own controls handle their Enter - intercepting would
        // both suppress the button and open a second dialog.
        if ((ev.target as HTMLElement)?.closest('button, a, [role="button"]'))
          return;

        ev.preventDefault();
        openFilePicker();
        return;
      }

      // A field Tab passed over without opening - Enter is how it is edited.
      if (!isEditing) {
        ev.preventDefault();
        pendingFocusRef.current = 'first';
        setIsEditing(true);
        return;
      }

      // Enter belongs to the field itself only where a newline is a real part
      // of the value - the business description. Every other editor here is a
      // one-line control, the list rows included, so Enter commits.
      if (allowEnter) return;

      ev.preventDefault();

      // A block of several controls - the list sections, the financial panel -
      // is walked through before the field is left.
      if (focusNextInnerControl()) return;

      setIsEditing(false);
      focusSiblingField(1);
    },
    [
      allowEnter,
      cancelEdit,
      focusNextInnerControl,
      focusSiblingField,
      isEditing,
      isImages,
      openFilePicker,
    ],
  );

  const focusInnerControl = useCallback((fromEnd: boolean) => {
    const controls = rootRef.current?.querySelectorAll<HTMLElement>(
      FOCUSABLE_CONTROL_SELECTOR,
    );

    if (!controls?.length) return;

    (fromEnd ? controls[controls.length - 1] : controls[0]).focus();
  }, []);

  /**
   * Lands on the control a focus request named, and touches the field so it
   * shows its own required error there and then. Without the touch the input
   * looks no different from the ones beside it, which is what "Show missing
   * fields" is being asked for.
   */
  const focusPendingField = useCallback(() => {
    const fieldName = pendingFocusFieldRef.current;

    if (!fieldName) return false;

    pendingFocusFieldRef.current = null;

    const control = rootRef.current?.querySelector<HTMLElement>(
      `[name="${fieldName}"]`,
    );

    if (!control) return false;

    // final-form marks touched on blur, so the pair has to run before the
    // control takes focus for itself.
    form.focus(fieldName);
    form.blur(fieldName);

    control.focus();
    return true;
  }, [form]);

  const onFocus = useCallback(
    (ev: React.FocusEvent<HTMLDivElement>) => {
      // Only the wrapper itself - focus moving between inner controls must not
      // restart the field.
      if (ev.target !== rootRef.current || isEditing) return;

      // Already answered: let Tab carry straight on rather than springing a
      // dropdown open over a value the broker did not come to change.
      if (keepClosedWhenFilled && !!value) return;

      pendingFocusRef.current = isShiftTabbingRef.current ? 'last' : 'first';
      setIsEditing(true);
    },
    [isEditing, keepClosedWhenFilled, value],
  );

  const onBlur = useCallback((ev: React.FocusEvent<HTMLDivElement>) => {
    const next = ev.relatedTarget as HTMLElement | null;

    // Focus went nowhere rather than somewhere else: the file dialog took the
    // window, or something non-focusable inside was clicked. Neither means the
    // broker left the field - clicking away is the overlay's job.
    if (!next) return;

    // Select options render in a portal outside this subtree.
    if (next.closest('.dropdown__content')) return;
    if (rootRef.current?.contains(next)) return;

    setIsEditing(false);
  }, []);

  /**
   * Saving is driven by leaving edit state rather than by blur: pressing Enter
   * and unmounting mid-edit both remove the input without firing a blur React
   * can see, and SelectField has no blur hook at all.
   */
  const queueSave = useCallback(
    (debounce: boolean) => {
      if (isSaveOnNone(saveOn)) return;
      if (isEqual(lastQueuedRef.current, value)) return;

      lastQueuedRef.current = value;
      saveFields({ [name]: value }, { debounce });
    },
    [name, saveFields, saveOn, value],
  );

  /**
   * Leaving edit state must send immediately. queueSave alone is not enough:
   * a debounced field has usually already queued its value, so the dedupe
   * short-circuits and the pending timer would keep the save waiting.
   */
  const commitSave = useCallback(() => {
    if (isSaveOnNone(saveOn)) return;

    queueSave(false);
    flushNow();
  }, [flushNow, queueSave, saveOn]);

  const commitSaveRef = useRef(commitSave);
  commitSaveRef.current = commitSave;

  /**
   * What Escape puts back. Taken as edit state flips, which covers every way
   * the field opens: clicking it, tabbing onto it, Enter, and a focus request.
   * Final-form values are replaced rather than mutated, so holding the object
   * costs nothing and keeps the whole snapshot honest.
   */
  useEffect(() => {
    if (isEditing) editStartValuesRef.current = form.getState().values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  useEffect(() => {
    if (isEditing) return;
    setRenderedChild(RenderPreview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }

    // The image editor swaps without the scale transition, so the file input
    // exists by the time Enter arrives and the dialog stays user-initiated.
    if (isImages) {
      setRenderedChild(getChild());
      return;
    }

    setTransitionClass('disappear');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, name]);

  useEffect(() => {
    if (isEditing && isSaveOnDebounced(saveOn)) queueSave(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // The editor mounts a render (or a transition) after isEditing flips, so the
  // control to focus does not exist at the point the field is opened.
  useEffect(() => {
    if (!isEditing) {
      pendingFocusRef.current = null;
      pendingFocusFieldRef.current = null;
      return;
    }

    // A named control wins over the first one: a focus request opens the field
    // only to reach the row that is actually outstanding.
    if (focusPendingField()) {
      pendingFocusRef.current = null;
      return;
    }

    if (!pendingFocusRef.current) return;

    const fromEnd = pendingFocusRef.current === 'last';
    pendingFocusRef.current = null;
    focusInnerControl(fromEnd);
  }, [focusInnerControl, focusPendingField, isEditing, renderedChild]);

  /**
   * The broker opened this field and left it. markIfMissing is a no-op on a
   * requirement that is already satisfied, so a field that was filled in - or
   * one whose rule the other sections already answer - earns no mark.
   */
  const markOnLeave = useCallback(
    () => requirementKeys?.forEach((key) => markIfMissing?.(key)),
    [markIfMissing, requirementKeys],
  );

  const markOnLeaveRef = useRef(markOnLeave);
  markOnLeaveRef.current = markOnLeave;

  useEffect(() => {
    if (wasEditing.current && !isEditing) {
      commitSave();

      // Escape restored the value on the way out, so there is nothing the
      // broker left half-done here to hold against them.
      if (!skipMarkRef.current) markOnLeaveRef.current();
      skipMarkRef.current = false;
    }
    wasEditing.current = isEditing;
  }, [isEditing, commitSave]);

  useEffect(
    () => () => {
      if (wasEditing.current) {
        commitSaveRef.current();
        markOnLeaveRef.current();
      }
    },
    [],
  );

  /**
   * "Show missing fields" scrolls here and opens the field, landing on the one
   * control that is outstanding where the field opens as a block of several.
   *
   * An already-open field has to be handled too: the wrapper is tabIndex -1 by
   * then and onFocus declines to reopen, so the pricing panel would take the
   * request and show nothing - the mark it would paint lives on the closed
   * preview.
   *
   * Pressing it again on the field already open is deliberately not skipped:
   * re-asserting the error is the whole point, and every step of it is a no-op
   * once it holds - focusing the focused control fires nothing, and the page is
   * already scrolled to it.
   */
  useEffect(() => {
    if (focusRequest?.fieldName !== name) return;

    consumeFocusRequest?.();

    rootRef.current?.scrollIntoView({
      ...SMOOTH_SCROLL_CONFIGURATION,
      block: 'center',
    });

    pendingFocusFieldRef.current = focusRequest.focusFieldName ?? null;

    if (isEditing) {
      focusPendingField();
      return;
    }

    pendingFocusRef.current = 'first';
    setIsEditing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumeFocusRequest, focusRequest, name]);

  return (
    <div
      ref={rootRef}
      className={classes}
      // Reachable when closed; once open the inner controls are the tab stops.
      tabIndex={isEditing ? -1 : 0}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      {...{ [FIELD_MARKER_ATTRIBUTE]: '', [FIELD_NAME_ATTRIBUTE]: name }}
    >
      <div
        className={contentClasses}
        onTransitionEnd={(e) => {
          // Scoped to transform: the hover background is also transitioned.
          if (e.target === e.currentTarget && e.propertyName === 'transform')
            onTransitionsEnd();
        }}
        onClick={(e) => {
          if (isEditing || isSliderButtonClicked(e)) return;

          pendingFocusRef.current = 'first';
          setIsEditing(true);
        }}
      >
        {renderedChild}
      </div>
      {isEditing && (
        <div
          className="detail-page-field__overlay"
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(false);
          }}
        />
      )}
    </div>
  );
};

export default DetailPageField;
